<template>
  <li :style="zoom">
    <div
      ref="player"
      class="player"
      :class="[
        {
          dead: player.isDead,
          marked: session.markedPlayer === index,
          'no-vote': player.isVoteless,
          you: session.sessionId && player.id && player.id === session.playerId,
          'vote-yes': session.votes[index],
          'vote-lock': voteLocked,
          'token-upside-down': isTokenUpsideDown,
          'night-active': this.nightNavigation.currentSeatIndex === this.index,
          'poisoned-active':
            this.statusEffectsEnabled &&
            this.hasPoisonReminder &&
            !this.hasDrunkReminder,
          'drunk-active':
            this.statusEffectsEnabled &&
            this.hasDrunkReminder &&
            !this.hasPoisonReminder,
          'drunk-poisoned-active':
            this.statusEffectsEnabled &&
            this.hasDrunkReminder &&
            this.hasPoisonReminder,
        },
        player.role.team,
      ]"
    >
      <div class="shroud" @click="toggleStatus()"></div>
      <div class="life" @click="toggleStatus()"></div>

      <div
        class="night-order first"
        v-if="nightOrder.get(player).first && grimoire.isNightOrder"
      >
        <em>{{ nightOrder.get(player).first }}.</em>
        <span v-if="player.role.firstNightReminder">{{
          player.role.firstNightReminder
        }}</span>
      </div>
      <div
        class="night-order other"
        v-if="nightOrder.get(player).other && grimoire.isNightOrder"
      >
        <em>{{ nightOrder.get(player).other }}.</em>
        <span v-if="player.role.otherNightReminder">{{
          player.role.otherNightReminder
        }}</span>
      </div>

      <Token
        :role="player.role"
        @set-role="$emit('trigger', ['openRoleModal'])"
      />

      <!-- Overlay icons -->
      <div class="overlay">
        <font-awesome-icon
          icon="hand-paper"
          class="vote"
          :title="$t('player.handUp')"
          @click="vote()"
        />
        <font-awesome-icon
          icon="times"
          class="vote"
          :title="$t('player.handDown')"
          @click="vote()"
        />
        <font-awesome-icon
          icon="times-circle"
          class="cancel"
          :title="$t('player.cancel')"
          @click="cancel()"
        />
        <font-awesome-icon
          icon="exchange-alt"
          class="swap"
          @click="swapPlayer(player)"
          :title="$t('player.swapWithPlayer')"
        />
        <font-awesome-icon
          icon="redo-alt"
          class="move"
          @click="movePlayer(player)"
          :title="$t('player.moveToSeat')"
        />
        <font-awesome-icon
          icon="hand-point-right"
          class="nominate"
          @click="nominatePlayer(player)"
          :title="$t('player.nominatePlayer')"
        />
      </div>

      <!-- Claimed seat icon -->
      <font-awesome-icon
        icon="chair"
        v-if="player.id && session.sessionId"
        class="seat"
        :class="{ highlight: session.isRolesDistributed }"
      />

      <!-- Ghost vote icon -->
      <font-awesome-icon
        icon="vote-yea"
        class="has-vote"
        v-if="player.isDead && !player.isVoteless"
        @click="updatePlayer('isVoteless', true)"
        :title="$t('player.ghostVote')"
      />

      <!-- On block icon -->
      <div class="marked">
        <font-awesome-icon icon="skull" />
      </div>
      <div class="name" @click="toggleMenu" :class="{ active: isMenuOpen }">
        <span>{{ displayName }}</span>
        <font-awesome-icon icon="venus-mars" v-if="player.pronouns" />
        <font-awesome-icon
          v-if="showVoiceStatusIcon"
          icon="volume-up"
          class="seat-voice-icon"
          :class="{
            'seat-speaking-icon': isVoiceSpeaking,
            'seat-voice-inactive': !isVoiceSpeaking,
          }"
          :title="$t('voice.speaking')"
        />
        <div class="pronouns" v-if="player.pronouns">
          <span>{{ player.pronouns }}</span>
        </div>
      </div>

      <transition name="fold">
        <ul
          class="menu"
          v-if="isMenuOpen"
          :class="menuPosition"
          :style="menuStyle"
          ref="menu"
        >
          <li
            @click="changePronouns"
            v-if="
              !session.isSpectator ||
              (session.isSpectator && player.id === session.playerId)
            "
          >
            <font-awesome-icon icon="venus-mars" />{{
              $t("player.changePronouns")
            }}
          </li>
          <template v-if="!session.isSpectator">
            <li @click="changeName">
              <font-awesome-icon icon="user-edit" />{{ $t("player.rename") }}
            </li>
            <li @click="toggleTokenUpsideDown">
              <font-awesome-icon icon="sync-alt" />{{ $t("player.flipToken") }}
            </li>
            <li @click="movePlayer()" :class="{ disabled: session.lockedVote }">
              <font-awesome-icon icon="redo-alt" />
              {{ $t("player.movePlayer") }}
            </li>
            <li @click="swapPlayer()" :class="{ disabled: session.lockedVote }">
              <font-awesome-icon icon="exchange-alt" />
              {{ $t("player.swapSeats") }}
            </li>
            <li @click="removePlayer" :class="{ disabled: session.lockedVote }">
              <font-awesome-icon icon="times-circle" />
              {{ $t("player.remove") }}
            </li>
            <li
              @click="updatePlayer('id', '', true)"
              v-if="player.id && session.sessionId"
            >
              <font-awesome-icon icon="chair" />
              {{ $t("player.emptySeat") }}
            </li>
            <template v-if="!session.nomination">
              <li @click="nominatePlayer()">
                <font-awesome-icon icon="hand-point-right" />
                {{ $t("player.nomination") }}
              </li>
            </template>
          </template>
          <li
            @click="claimSeat"
            v-if="session.isSpectator"
            :class="{ disabled: player.id && player.id !== session.playerId }"
          >
            <font-awesome-icon icon="chair" />
            <template v-if="!player.id">
              {{ $t("player.claimSeat") }}
            </template>
            <template v-else-if="player.id === session.playerId">
              {{ $t("player.vacateSeat") }}
            </template>
            <template v-else> {{ $t("player.seatOccupied") }}</template>
          </li>
        </ul>
      </transition>
    </div>

    <template v-if="player.reminders">
      <div
        class="reminder"
        :key="
          reminder.role + ' ' + reminder.name + ' ' + (reminder.image || '')
        "
        v-for="reminder in player.reminders"
        :class="[reminder.role]"
        @click="removeReminder(reminder)"
      >
        <span
          class="icon"
          :style="{
            backgroundImage: `url(${displayReminderImage(reminder)})`,
          }"
        ></span>
        <span class="text">{{ displayReminderName(reminder) }}</span>
      </div>
    </template>
    <div class="reminder add" @click="$emit('trigger', ['openReminderModal'])">
      <span class="icon"></span>
    </div>
    <div class="reminderHoverTarget"></div>
  </li>
</template>

<script>
import Token from "./Token";
import { mapGetters, mapState } from "vuex";
import { reminderIconImage } from "../utils/roleIcon";

const { translateOfficialReminder } = require("../utils/reminderTranslations");

export default {
  components: {
    Token,
  },
  props: {
    player: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState("players", ["players", "nightNavigation"]),
    ...mapState(["grimoire", "session", "voice"]),
    ...mapGetters({ nightOrder: "players/nightOrder" }),
    index: function () {
      return this.players.indexOf(this.player);
    },
    displayName: function () {
      if (!this.player.name) return "";
      return `${this.index + 1}.${this.player.name}`;
    },
    showVoiceStatusIcon: function () {
      return !!(this.session.sessionId && this.player.id);
    },
    isVoiceSpeaking: function () {
      if (!this.player.id || !this.voice || !this.voice.state) return false;
      if (
        this.player.id === this.session.playerId &&
        this.session.isSpectator &&
        this.voice.speaking
      ) {
        return true;
      }
      const participant = this.voice.state.participants.find(
        (participant) => participant.id === this.player.id,
      );
      return !!(participant && participant.speaking);
    },
    reminderStatusEffects: function () {
      const poisonRoles = [
        "poisoner",
        "pukka",
        "vigormortis",
        "nodashii",
        "lleech",
        "widow",
        "snakecharmer",
      ];
      const drunkRoles = [
        "drunk",
        "sailor",
        "innkeeper",
        "courtier",
        "philosopher",
        "puzzlemaster",
        "sweetheart",
      ];
      const poisonTextSignals = [
        "Poisoned",
        "poison",
        "中毒",
        "毒",
        "普卡毒",
        "亡骨魔毒",
      ];
      const drunkTextSignals = ["Drunk", "drunk", "醉酒", "酒鬼", "是酒鬼"];
      const effects = { poison: false, drunk: false };

      (this.player.reminders || []).forEach((reminder) => {
        const name = `${reminder.name || ""}`;
        const role = `${reminder.role || ""}`;
        const hasPoisonText = poisonTextSignals.some((signal) =>
          name.includes(signal),
        );
        const hasDrunkText = drunkTextSignals.some((signal) =>
          name.includes(signal),
        );

        if (
          hasPoisonText &&
          (poisonRoles.includes(role) || !drunkRoles.includes(role))
        ) {
          effects.poison = true;
        }
        if (
          hasDrunkText &&
          (drunkRoles.includes(role) || !poisonRoles.includes(role))
        ) {
          effects.drunk = true;
        }
      });

      return effects;
    },
    hasPoisonReminder: function () {
      return this.reminderStatusEffects.poison;
    },
    hasDrunkReminder: function () {
      return this.reminderStatusEffects.drunk;
    },
    statusEffectsEnabled: function () {
      return this.grimoire.statusEffectsEnabled !== false;
    },
    menuStyle: function () {
      return this.menuOffsetY
        ? { transform: `translateY(${this.menuOffsetY}px)` }
        : {};
    },
    voteLocked: function () {
      const session = this.session;
      const players = this.players.length;
      if (!session.nomination) return false;
      const indexAdjusted =
        (this.index - 1 + players - session.nomination[1]) % players;
      return indexAdjusted < session.lockedVote - 1;
    },
    zoom: function () {
      const unit = window.innerWidth > window.innerHeight ? "vh" : "vw";
      const isPortraitMobile =
        window.innerWidth <= 768 && window.innerWidth <= window.innerHeight;
      if (isPortraitMobile) {
        if (this.players.length < 7) {
          return { width: 16.5 + Math.min(this.grimoire.zoom, 2) + unit };
        } else if (this.players.length <= 10) {
          return { width: 15 + Math.min(this.grimoire.zoom, 2) + unit };
        } else if (this.players.length <= 15) {
          return { width: 12.6 + Math.min(this.grimoire.zoom, 2) + unit };
        } else {
          return { width: 11.2 + Math.min(this.grimoire.zoom, 2) + unit };
        }
      }
      if (this.players.length < 7) {
        return { width: 18 + this.grimoire.zoom + unit };
      } else if (this.players.length <= 10) {
        return { width: 16 + this.grimoire.zoom + unit };
      } else if (this.players.length <= 15) {
        return { width: 14 + this.grimoire.zoom + unit };
      } else {
        return { width: 12 + this.grimoire.zoom + unit };
      }
    },
  },
  data() {
    return {
      isMenuOpen: false,
      isSwap: false,
      isTokenUpsideDown: false,
      menuPosition: {
        left: false,
        down: false,
      },
      menuOffsetY: 0,
    };
  },
  watch: {
    isMenuOpen(open) {
      if (!open) this.resetMenuPosition();
    },
  },
  methods: {
    displayReminderImage(reminder) {
      return reminderIconImage(reminder, this.grimoire.isImageOptIn);
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      this.resetMenuPosition();
      if (this.isMenuOpen) this.$nextTick(this.adjustMenuPosition);
    },
    resetMenuPosition() {
      this.menuPosition = {
        left: false,
        down: false,
      };
      this.menuOffsetY = 0;
    },
    adjustMenuPosition() {
      const menu = this.$refs.menu;
      if (!menu) return;
      const bounds = menu.getBoundingClientRect();
      const padding = 8;
      this.menuPosition = {
        left: bounds.right > window.innerWidth,
        down: bounds.top < padding,
      };
      requestAnimationFrame(() => {
        const nextBounds = menu.getBoundingClientRect();
        if (nextBounds.top < padding) {
          this.menuOffsetY = padding - nextBounds.top;
        } else if (nextBounds.bottom > window.innerHeight - padding) {
          this.menuOffsetY = window.innerHeight - padding - nextBounds.bottom;
        }
      });
    },
    toggleTokenUpsideDown() {
      this.isTokenUpsideDown = !this.isTokenUpsideDown;
      this.isMenuOpen = false;
    },
    changePronouns() {
      if (this.session.isSpectator && this.player.id !== this.session.playerId)
        return;
      const pronouns = prompt(
        this.$t("player.promptPronouns"),
        this.player.pronouns,
      );
      //Only update pronouns if not null (prompt was not cancelled)
      if (pronouns !== null) {
        this.updatePlayer("pronouns", pronouns, true);
      }
    },
    toggleStatus() {
      if (this.grimoire.isPublic) {
        if (!this.player.isDead) {
          this.updatePlayer("isDead", true);
          if (this.player.isMarked) {
            this.updatePlayer("isMarked", false);
          }
        } else if (this.player.isVoteless) {
          this.updatePlayer("isVoteless", false);
          this.updatePlayer("isDead", false);
        } else {
          this.updatePlayer("isVoteless", true);
        }
      } else {
        this.updatePlayer("isDead", !this.player.isDead);
        if (this.player.isMarked) {
          this.updatePlayer("isMarked", false);
        }
        if (this.player.isVoteless) {
          this.updatePlayer("isVoteless", false);
        }
      }
    },
    changeName() {
      if (this.session.isSpectator) return;
      const name =
        prompt(this.$t("player.promptName"), this.player.name) ||
        this.player.name;
      this.updatePlayer("name", name, true);
    },
    removeReminder(reminder) {
      const reminders = [...this.player.reminders];
      reminders.splice(this.player.reminders.indexOf(reminder), 1);
      this.updatePlayer("reminders", reminders, true);
    },
    displayReminderName(reminder) {
      return translateOfficialReminder(reminder && reminder.name);
    },
    updatePlayer(property, value, closeMenu = false) {
      if (
        this.session.isSpectator &&
        property !== "reminders" &&
        property !== "pronouns"
      )
        return;
      this.$store.commit("players/update", {
        player: this.player,
        property,
        value,
      });
      if (closeMenu) {
        this.isMenuOpen = false;
      }
    },
    removePlayer() {
      this.isMenuOpen = false;
      this.$emit("trigger", ["removePlayer"]);
    },
    swapPlayer(player) {
      this.isMenuOpen = false;
      this.$emit("trigger", ["swapPlayer", player]);
    },
    movePlayer(player) {
      this.isMenuOpen = false;
      this.$emit("trigger", ["movePlayer", player]);
    },
    nominatePlayer(player) {
      this.isMenuOpen = false;
      this.$emit("trigger", ["nominatePlayer", player]);
    },
    cancel() {
      this.$emit("trigger", ["cancel"]);
    },
    claimSeat() {
      this.isMenuOpen = false;
      this.$emit("trigger", ["claimSeat"]);
    },
    /**
     * Allow the ST to override a locked vote.
     */
    vote() {
      if (this.session.isSpectator) return;
      if (!this.voteLocked) return;
      this.$store.commit("session/voteSync", [
        this.index,
        !this.session.votes[this.index],
      ]);
    },
  },
};
</script>

<style lang="scss">
@import "../vars.scss";

.fold-enter-active,
.fold-leave-active {
  transition: transform 250ms ease-in-out;
  transform-origin: left center;
  transform: perspective(200px);
}
.fold-enter,
.fold-leave-to {
  transform: perspective(200px) rotateY(90deg);
}

/***** Player token *****/
.circle .player {
  margin-bottom: 10px;

  &:before {
    content: " ";
    display: block;
    padding-top: 100%;
  }

  .shroud {
    top: 0;
    left: 0;
    position: absolute;
    width: 100%;
    height: 45%;
    cursor: pointer;
    transform: rotateX(0deg);
    transform-origin: top center;
    transition: transform 200ms ease-in-out;
    z-index: 2;
    filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.8));

    &:before {
      content: " ";
      background: url("../assets/shroud.png") center -10px no-repeat;
      background-size: auto 110%;
      position: absolute;
      margin-left: -50%;
      width: 100%;
      height: 100%;
      left: 50%;
      top: -30%;
      opacity: 0;
      transform: perspective(400px) scale(1.5);
      transform-origin: top center;
      transition: all 200ms;
      pointer-events: none;
    }

    #townsquare.spectator & {
      pointer-events: none;
    }

    #townsquare:not(.spectator) &:hover:before {
      opacity: 0.5;
      top: -10px;
      transform: scale(1);
    }
  }

  &.dead .shroud:before {
    opacity: 1;
    top: 0;
    transform: perspective(400px) scale(1);
  }

  #townsquare:not(.spectator) &.dead .shroud:hover:before {
    opacity: 1;
  }
}

/****** Life token *******/
.player {
  z-index: 2;
  .life {
    border-radius: 50%;
    width: 100%;
    background: url("../assets/life1.png") center center;
    background-size: 100%;
    border: 3px solid black;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
    cursor: pointer;
    transition: transform 200ms ease-in-out;
    transform: perspective(400px) rotateY(180deg);
    backface-visibility: hidden;
    position: absolute;
    left: 0;
    top: 0;

    &:before {
      content: " ";
      display: block;
      padding-top: 100%;
    }
  }

  &.dead {
    &.no-vote .life:after {
      display: none;
    }

    .life {
      background-image: url("../assets/death.png");

      &:after {
        content: " ";
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: url("../assets/vote.png") center center no-repeat;
        background-size: 50%;
        height: 100%;
        pointer-events: none;
      }
    }
  }

  &.traveler .life {
    filter: grayscale(100%);
  }
}

#townsquare.public .player {
  .shroud {
    transform: perspective(400px) rotateX(90deg);
    pointer-events: none;
  }

  .life {
    transform: perspective(400px) rotateY(0deg);
  }

  &.traveler:not(.dead) .token {
    transform: perspective(400px) scale(0.8);
    pointer-events: none;
    transition-delay: 0s;
  }

  &.traveler.dead .token {
    transition-delay: 0s;
  }
}

/***** Role token ******/
.player .token {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  transition: transform 200ms ease-in-out;
  transform: perspective(400px) rotateY(0deg);
  backface-visibility: hidden;
}

.player.night-active .token {
  animation: nightActionPulse 1.6s ease-in-out infinite;
}

.player.poisoned-active .token {
  animation: poisonTokenPulse 2.25s ease-in-out infinite;
}

.player.drunk-active .token {
  animation:
    drunkTokenWobble 2.15s ease-in-out infinite,
    drunkTokenBreath 1.9s ease-in-out infinite;
}

.player.drunk-poisoned-active .token {
  animation:
    drunkTokenWobble 2.15s ease-in-out infinite,
    drunkTokenBreath 1.9s ease-in-out infinite,
    poisonTokenPulse 2.25s ease-in-out infinite;
}

.player.poisoned-active .token:before,
.player.drunk-poisoned-active .token:before {
  content: "";
  position: absolute;
  inset: 8%;
  border-radius: 50%;
  background: radial-gradient(
      circle at 35% 35%,
      rgba(64, 240, 92, 0.48),
      transparent 18%
    ),
    radial-gradient(circle at 67% 65%, rgba(24, 145, 68, 0.42), transparent 23%);
  mix-blend-mode: multiply;
  animation: sickWash 2.85s ease-in-out infinite;
  pointer-events: none;
  z-index: 3;
}

.player.drunk-active .token:after,
.player.drunk-poisoned-active .token:after {
  content: "";
  position: absolute;
  inset: -5%;
  border: 4px solid rgba(91, 190, 255, 0.18);
  border-radius: 50%;
  filter: blur(1px);
  animation: drunkTokenAfterimage 2.15s ease-in-out infinite;
  pointer-events: none;
}

.player.night-active.poisoned-active .token,
.player.night-active.drunk-active .token,
.player.night-active.drunk-poisoned-active .token {
  animation: nightActionPulse 1.6s ease-in-out infinite;
}

.player.night-active.poisoned-active .token:before,
.player.night-active.drunk-poisoned-active .token:before,
.player.night-active.drunk-active .token:after,
.player.night-active.drunk-poisoned-active .token:after {
  content: none;
}

@keyframes nightActionPulse {
  0%,
  100% {
    filter: drop-shadow(0 0 7px rgba(255, 226, 46, 0.72))
      drop-shadow(0 0 18px rgba(255, 245, 120, 0.46));
  }
  50% {
    filter: drop-shadow(0 0 13px rgba(255, 226, 46, 0.96))
      drop-shadow(0 0 30px rgba(255, 245, 120, 0.72));
  }
}

@keyframes poisonTokenPulse {
  0%,
  100% {
    filter: drop-shadow(0 0 5px rgba(60, 230, 88, 0.26));
  }
  50% {
    filter: drop-shadow(0 0 15px rgba(60, 230, 88, 0.62));
  }
}

@keyframes sickWash {
  0%,
  100% {
    opacity: 0.26;
    transform: translate(-4px, 3px) scale(0.95);
  }
  50% {
    opacity: 0.68;
    transform: translate(5px, -3px) scale(1.04);
  }
}

@keyframes drunkTokenWobble {
  0%,
  100% {
    transform: perspective(400px) rotateY(0deg) rotate(-2.5deg)
      translate(-1px, 0);
  }
  50% {
    transform: perspective(400px) rotateY(0deg) rotate(3deg)
      translate(2px, -2px);
  }
}

@keyframes drunkTokenBreath {
  0%,
  100% {
    filter: drop-shadow(0 0 8px rgba(82, 185, 255, 0.58))
      drop-shadow(0 0 15px rgba(126, 103, 255, 0.36));
  }
  50% {
    filter: drop-shadow(0 0 16px rgba(82, 185, 255, 0.58))
      drop-shadow(0 0 30px rgba(126, 103, 255, 0.36));
  }
}

@keyframes drunkTokenAfterimage {
  0%,
  100% {
    opacity: 0.18;
    transform: translateX(7px);
  }
  50% {
    opacity: 0.34;
    transform: translateX(-6px);
  }
}

.player.token-upside-down .token {
  transform: perspective(400px) rotateY(0deg) rotate(180deg);
}

#townsquare.public .circle .token {
  transform: perspective(400px) rotateY(-180deg);
}

#townsquare.public .circle .player.token-upside-down .token {
  transform: perspective(400px) rotateY(-180deg) rotate(180deg);
}

#townsquare.public .circle .player.poisoned-active .token,
#townsquare.public .circle .player.drunk-active .token,
#townsquare.public .circle .player.drunk-poisoned-active .token {
  animation: none;
  filter: none;
}

/****** Player choice icons *******/
.player .overlay {
  width: 100%;
  position: absolute;
  pointer-events: none;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:after {
    content: " ";
    display: block;
    padding-top: 100%;
  }
}
.player .overlay svg {
  position: absolute;
  filter: drop-shadow(0 0 3px black);
  z-index: 2;
  cursor: pointer;
  &.swap,
  &.move,
  &.nominate,
  &.vote,
  &.cancel {
    width: 50%;
    height: 60%;
    opacity: 0;
    pointer-events: none;
    transition: all 250ms;
    transform: scale(0.2);
    * {
      stroke-width: 10px;
      stroke: white;
      fill: url(#default);
    }
    &:hover *,
    &.fa-hand-paper * {
      fill: url(#demon);
    }
    &.fa-times * {
      fill: url(#townsfolk);
    }
  }
}

// other player voted yes, but is not locked yet
#townsquare.vote .player.vote-yes .overlay svg.vote.fa-hand-paper {
  opacity: 0.5;
  transform: scale(1);
}

// you voted yes | a locked vote yes | a locked vote no
#townsquare.vote .player.you.vote-yes .overlay svg.vote.fa-hand-paper,
#townsquare.vote .player.vote-lock.vote-yes .overlay svg.vote.fa-hand-paper,
#townsquare.vote .player.vote-lock:not(.vote-yes) .overlay svg.vote.fa-times {
  opacity: 1;
  transform: scale(1);
}

// a locked vote can be clicked on by the ST
#townsquare.vote:not(.spectator) .player.vote-lock .overlay svg.vote {
  pointer-events: all;
}

li.from:not(.nominate) .player .overlay svg.cancel {
  opacity: 1;
  transform: scale(1);
  pointer-events: all;
}

li.swap:not(.from) .player .overlay svg.swap,
li.nominate .player .overlay svg.nominate,
li.move:not(.from) .player .overlay svg.move {
  opacity: 1;
  transform: scale(1);
  pointer-events: all;
}

/****** Vote icon ********/
.player .has-vote {
  color: #fff;
  filter: drop-shadow(0 0 3px black);
  transition: opacity 250ms;
  z-index: 2;

  #townsquare.public & {
    opacity: 0;
    pointer-events: none;
  }
}

.has-vote {
  position: absolute;
  margin-top: -15%;
  right: 2px;
}

/****** Session seat glow *****/
@mixin glow($name, $color) {
  @keyframes #{$name}-glow {
    0% {
      box-shadow: 0 0 rgba($color, 1);
      border-color: $color;
    }
    50% {
      border-color: black;
    }
    100% {
      box-shadow: 0 0 20px 16px transparent;
      border-color: $color;
    }
  }

  .player.you.#{$name} .token {
    animation: #{$name}-glow 5s ease-in-out infinite;
  }
}

@include glow("townsfolk", $townsfolk);
@include glow("outsider", $outsider);
@include glow("demon", $demon);
@include glow("minion", $minion);
@include glow("traveler", $traveler);

.player.you .token {
  animation: townsfolk-glow 5s ease-in-out infinite;
}

/****** Marked icon ******/
.player .marked {
  position: absolute;
  width: 100%;
  top: 0;
  filter: drop-shadow(0px 0px 6px black);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 250ms;
  opacity: 0;
  &:before {
    content: " ";
    padding-top: 100%;
    display: block;
  }
  svg {
    height: 60%;
    width: 60%;
    position: absolute;
    stroke: white;
    stroke-width: 15px;
    path {
      fill: white;
    }
  }
}
.player.marked .marked {
  opacity: 0.5;
}

/****** Seat icon ********/
.player .seat {
  position: absolute;
  left: 2px;
  margin-top: -15%;
  color: #fff;
  filter: drop-shadow(0 0 3px black);
  cursor: default;
  z-index: 2;
  &.highlight {
    animation-iteration-count: 1;
    animation: redToWhite 1s normal forwards;
  }
}

// highlight animation
@keyframes redToWhite {
  from {
    color: $demon;
  }
  to {
    color: white;
  }
}

.player.you .seat {
  color: $townsfolk;
}

/***** Player name *****/
.player > .name {
  position: relative;
  right: 10%;
  display: flex;
  justify-content: center;
  font-size: 120%;
  line-height: 120%;
  cursor: pointer;
  white-space: nowrap;
  width: 120%;
  background: rgba(0, 0, 0, 0.5);
  border: 3px solid black;
  border-radius: 10px;
  top: 5px;
  box-shadow: 0 0 5px black;
  padding: 0 4px;

  svg {
    top: 3px;
    margin-right: 2px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
    flex-grow: 1;
  }

  #townsquare:not(.spectator) &:hover,
  &.active {
    color: red;
  }

  &:hover .pronouns {
    opacity: 1;
    color: white;
  }

  .pronouns {
    display: flex;
    position: absolute;
    right: 110%;
    max-width: 250px;
    z-index: 25;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 10px;
    border: 3px solid black;
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.5));
    align-items: center;
    pointer-events: none;
    opacity: 0;
    transition: opacity 200ms ease-in-out;
    padding: 0 4px;
    bottom: -3px;

    &:before {
      content: " ";
      border: 10px solid transparent;
      width: 0;
      height: 0;
      border-left-color: black;
      position: absolute;
      margin-left: 2px;
      left: 100%;
    }
  }
}

.player > .name .seat-voice-icon {
  position: absolute;
  top: -1.08em;
  right: -0.18em;
  width: 1em;
  height: 1em;
  pointer-events: none;
  z-index: 20;
}

.player > .name .seat-speaking-icon {
  color: #ffd94a;
  filter: drop-shadow(0 0 3px black)
    drop-shadow(0 0 7px rgba(255, 217, 74, 0.85));
  animation: voice-speaking-pulse 0.72s ease-in-out infinite;
}

.player > .name .seat-voice-inactive {
  color: #d64535;
  filter: drop-shadow(0 0 3px black)
    drop-shadow(0 0 5px rgba(214, 69, 53, 0.78));
  opacity: 0.88;
}

@keyframes voice-speaking-pulse {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

.player.dead > .name {
  opacity: 0.5;
}

/***** Player menu *****/
.player > .menu {
  position: absolute;
  left: 110%;
  bottom: -5px;
  text-align: left;
  white-space: nowrap;
  background: rgba(0, 0, 0, 0.5);
  padding: 2px 5px;
  border-radius: 10px;
  border: 3px solid #000;
  margin-left: 15px;
  cursor: pointer;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);

  &.left {
    left: auto;
    right: 110%;
    margin-left: 0;
    margin-right: 15px;
  }

  &.down {
    top: -5px;
    bottom: auto;
  }

  &:before {
    content: " ";
    width: 0;
    height: 0;
    position: absolute;
    border: 10px solid transparent;
    border-right-color: black;
    right: 100%;
    bottom: 5px;
    margin-right: 2px;
  }

  &.left:before {
    border-right-color: transparent;
    border-left-color: black;
    right: auto;
    left: 100%;
    margin-right: 0;
    margin-left: 2px;
  }

  &.down:before {
    top: 5px;
    bottom: auto;
  }

  li:hover {
    color: red;
  }

  li.disabled {
    cursor: not-allowed;
    opacity: 0.5;
    &:hover {
      color: white;
    }
  }

  svg {
    margin-right: 2px;
  }
}

/***** Ability text *****/
#townsquare.public .circle .ability {
  display: none;
}
.circle .player .shroud:hover ~ .token .ability,
.circle .player .token:hover .ability {
  opacity: 1;
}

/**** Night reminders ****/
.player .night-order {
  z-index: 3;
}

.player.dead .night-order em {
  color: #ddd;
  background: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, gray 100%);
}

/***** Reminder token *****/
.circle .reminder {
  background: url("../assets/reminder.png") center center;
  background-size: 100%;
  width: 50%;
  height: 0;
  padding-bottom: 50%;
  box-sizing: content-box;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 5px 0 0 -25%;
  border-radius: 50%;
  border: 3px solid black;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  transition: all 200ms;
  cursor: pointer;

  .text {
    line-height: 90%;
    color: black;
    font-size: 50%;
    font-weight: bold;
    text-align: center;
    margin-top: 50%;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 15%;
    text-shadow:
      0 1px 1px #f6dfbd,
      0 -1px 1px #f6dfbd,
      1px 0 1px #f6dfbd,
      -1px 0 1px #f6dfbd;
  }

  .icon,
  &:after {
    content: " ";
    position: absolute;
    top: 0;
    width: 90%;
    height: 90%;
    background-size: 100%;
    background-position: center 0;
    background-repeat: no-repeat;
    background-image: url("../assets/icons/plus.png");
    transition: opacity 200ms;
  }

  &:after {
    background-image: url("../assets/icons/x.png");
    opacity: 0;
    top: 5%;
  }

  &.add {
    opacity: 0;
    top: 30px;
    &:after {
      display: none;
    }
    .icon {
      top: 5%;
    }
  }

  &.custom {
    .icon {
      display: none;
    }
    .text {
      font-size: 70%;
      word-break: break-word;
      margin-top: 0;
      display: flex;
      align-items: center;
      align-content: center;
      justify-content: center;
      border-radius: 50%;
      top: 0;
    }
  }

  &:hover:before {
    opacity: 0;
  }
  &:hover:after {
    opacity: 1;
  }
}

.circle .reminderHoverTarget {
  opacity: 0;
  width: calc(50% + 8px);
  padding-top: calc(50% + 38px);
  margin-top: calc(-25% - 33px);
  margin-left: calc(-25% - 1px);
  border-radius: 0 0 999px 999px;
  pointer-events: auto;
  transform: none !important;
  z-index: -1;
}

.circle .player:hover ~ .reminder.add,
.circle .reminder.add:hover {
  opacity: 1;
  top: 0;
}
.circle .player:hover ~ .reminder.add:before,
.circle .reminder.add:hover:before {
  opacity: 1;
}

#townsquare.public .reminder {
  opacity: 0;
  pointer-events: none;
}
</style>
